import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import { RootState } from "src/redux/store";
import { listCargoClaims } from "src/app/Cargo/cargoAppSlice";
import { TableProps } from "@amzn/awsui-components-console";
import { MetricsPublisher } from "src/metrics";
import { CargoClaimSummary } from "@amzn/ttechclaimintakeservice-client";
import SearchResults from "../searchResults/SearchResults";
import ExportToCSVButton from "../searchResults/ExportToCSVButton";
import ClaimsSearchBar from "../claimsSearchBar/ClaimsSearchBar";
import { Flashbar, FlashbarProps } from "@amzn/awsui-components-console";
import SimLinkGenerator from "../simLinkGenerator/SimLinkGenerator";
import { SIM_TYPES } from "../simLinkGenerator/types";
import { SEARCH_PARAMETERS } from "../searchResults/constants";
import { CARGO_SEARCH_CONFIG } from "src/claimsHistory/configs/cargo";
import { CARGO_LINE_OF_BUSINESS_CONFIG, CLAIMS_LIMIT } from "./constants";
import { BUSINESS_UNIT_OPTIONS } from "../../forms/Cargo/options";
import { EMPTY_STRING_INDICATOR } from "../../app/constants";

//Declare metricsPublishers, they will be initialized on first render
let metricsPublisher: MetricsPublisher;
let metricsPublisherSearchByVrid: MetricsPublisher;

interface SearchParameters {
  searchBy: string;
  searchTerm: string;
  startDate?: number;
  endDate?: number;
}

export interface CargoClaimsHistoryProps {
  /** Whether to show only claims submitted by the current user */
  showUserClaimsOnly?: boolean;
  /** Custom test ID for the component */
  testId?: string;
}

// Column definitions for Cargo claims based on requirements
const CARGO_CLAIM_COLUMN_DEFINITIONS: TableProps.ColumnDefinition<CargoClaimSummary>[] = [
  {
    id: "vridLoadId",
    header: "VRID",
    cell: (item) => item.vridLoadId ?? EMPTY_STRING_INDICATOR,
    sortingField: "vridLoadId",
  },
  {
    id: "claimNumber",
    header: "WKW Reference",
    cell: (item) => item.claimNumber ?? EMPTY_STRING_INDICATOR,
    sortingField: "claimNumber",
  },
  {
    id: "businessUnit",
    header: "Business unit",
    cell: (item) =>
      item.businessUnit
        ? (BUSINESS_UNIT_OPTIONS.find((option) => item.businessUnit === option.value)?.label ??
          "Not found")
        : EMPTY_STRING_INDICATOR,
    sortingField: "businessUnit",
  },
  {
    id: "claimSubmitterName",
    header: "Claim submitter name",
    cell: (item) => {
      const firstName = item.claimReporterFirstName ?? "";
      const lastName = item.claimReporterLastName ?? "";
      return firstName && lastName ? `${firstName} ${lastName}` : EMPTY_STRING_INDICATOR;
    },
    sortingField: "claimReporterFirstName",
  },
  {
    id: "purchaseOrderNumber",
    header: "PO#",
    cell: (item) => item.purchaseOrderNumber ?? EMPTY_STRING_INDICATOR,
    sortingField: "purchaseOrderNumber",
  },
  {
    id: "createdAt",
    header: "Date claim reported",
    cell: (item) =>
      item.createdAtTimestamp
        ? new Date(item.createdAtTimestamp * 1000).toISOString().split("T")[0]
        : EMPTY_STRING_INDICATOR,
    sortingField: "createdAt",
  },
  {
    id: "incidentDate",
    header: "Date of loss",
    cell: (item) => item.incidentDate ?? EMPTY_STRING_INDICATOR,
    sortingField: "incidentDate",
  },
  {
    id: "totalClaimCurrencyCode",
    header: "Claim currency",
    cell: (item) => item.totalClaimCurrencyCode ?? EMPTY_STRING_INDICATOR,
    sortingField: "totalClaimCurrencyCode",
  },
];

// CSV headers for Cargo claims export
const CARGO_CSV_HEADERS = [
  { label: "VRID", key: "vridLoadId" },
  { label: "WKW Reference", key: "claimNumber" },
  { label: "Business unit", key: "businessUnit" },
  { label: "Claim submitter name", key: "claimSubmitterName" },
  { label: "PO#", key: "purchaseOrderNumber" },
  { label: "Date claim reported", key: "createdAt" },
  { label: "Date of loss", key: "incidentDate" },
  { label: "Claim currency", key: "totalClaimCurrencyCode" },
];

/**
 * Cargo specific implementation of claims history search
 * Uses the generic SearchResults component with Cargo-specific configuration
 * @param props CargoClaimsHistoryProps
 * @returns CargoClaimsHistory component
 */
function CargoClaimsHistory({
  showUserClaimsOnly = false,
  testId = "cargo-claims-search-results",
}: CargoClaimsHistoryProps) {
  //Redux state
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { user } = useAppSelector((state: RootState) => state.auth);
  const { cargoClaimsList, fetchingClaimsList, listCargoClaimsError } = useAppSelector(
    (state: RootState) => state.cargoApp
  );

  const [urlSearchParams] = useSearchParams();
  const [flashbarItems, setFlashBarItems] = useState<FlashbarProps.MessageDefinition[]>([]);

  // Use mock data for now since API might not support all required fields
  const [displayData, setDisplayData] = useState<CargoClaimSummary[]>([]);

  // The search bar is now controlled, so its selection and term live here. Every
  // searchable field id in the Cargo config is a SEARCH_PARAMETERS value, which
  // is what the metrics publisher below keys off.
  const [searchFieldId, setSearchFieldId] = useState<SEARCH_PARAMETERS>(
    CARGO_SEARCH_CONFIG.searchableFields[0].fieldId as SEARCH_PARAMETERS
  );
  const [searchInput, setSearchInput] = useState("");

  // Cargo-specific search callback
  const handleSearch = (searchParams: SearchParameters) => {
    // For now, just navigate to update URL - in future this would dispatch to cargo slice
    const searchRoute =
      CARGO_LINE_OF_BUSINESS_CONFIG.routePath +
      `?${searchParams.searchBy}=${searchParams.searchTerm}`;
    navigate(searchRoute, { replace: true });

    // Trigger search with the search parameters
    triggerSearch(searchParams);
  };

  const handleSubmit = () => {
    publishMetrics(searchInput, searchFieldId);
    handleSearch({ searchBy: searchFieldId, searchTerm: searchInput });
  };

  // Cargo-specific metrics callback
  const publishMetrics = (searchTerm: string, searchBy: SEARCH_PARAMETERS) => {
    // Use generic counter metrics for cargo search tracking
    if (searchBy === SEARCH_PARAMETERS.VRID_LOAD_ID) {
      metricsPublisherSearchByVrid.publishNamedCounterMetric("cargoSearchByVrid", 1);
    }
    metricsPublisher.publishNamedCounterMetric("cargoSearchBarUsed", 1);
  };

  const triggerSearch = (searchParameters: SearchParameters) => {
    const { searchTerm } = searchParameters;

    // TODO: This code below is to implement get claims by submitter alias
    // if (showUserClaimsOnly && user.id) {
    //   dispatch(
    //     listCargoClaims({
    //       input: {},
    //     })
    //   );
    //   return;
    // }
    dispatch(
      listCargoClaims({
        input: {
          vridLoadId: searchTerm,
        },
      })
    );
  };

  useEffect(() => {
    // Initialize metrics publishers
    metricsPublisher = new MetricsPublisher("cargoSearch");
    metricsPublisherSearchByVrid = new MetricsPublisher("cargoSearchByVrid");

    if (showUserClaimsOnly) {
      // For user claims, trigger search when user is loaded
      if (user.id) {
        triggerSearch({ searchBy: "", searchTerm: "" });

        // Initialize metrics for user claims
        metricsPublisher = new MetricsPublisher("mySubmittedCargoClaims");
        metricsPublisher.publishMySubmittedClaimsVisited();
      }
    } else {
      // Regular search results logic
      const vridLoadId = urlSearchParams.get("vridLoadId");
      const searchParameters: SearchParameters = {
        searchBy: SEARCH_PARAMETERS.VRID_LOAD_ID,
        searchTerm: vridLoadId ?? "",
      };
      // Seed the controlled bar, so a reloaded or shared results link shows the
      // term it is displaying results for rather than an empty box. The bar used
      // to read this query parameter itself.
      if (vridLoadId) {
        setSearchFieldId(SEARCH_PARAMETERS.VRID_LOAD_ID);
        setSearchInput(vridLoadId);
      }
      triggerSearch(searchParameters);

      metricsPublisher = new MetricsPublisher("cargoSearchResultsVisited");
    }
  }, [showUserClaimsOnly, user.id, urlSearchParams]);

  // Update display data when Redux state changes
  useEffect(() => {
    if (cargoClaimsList && cargoClaimsList.length > 0) {
      setDisplayData(cargoClaimsList);
    }
  }, [cargoClaimsList]);

  // Set up error flashbar for user claims
  useEffect(() => {
    if (showUserClaimsOnly && listCargoClaimsError) {
      setFlashBarItems([
        {
          type: "error",
          dismissible: true,
          dismissLabel: "Dismiss message",
          content: (
            <span>
              There was an error loading your submitted cargo claims. Please reload the page. If the
              error persists please open a{" "}
              <SimLinkGenerator
                simType={SIM_TYPES.REPORT_A_BUG}
                target="_blank"
                ariaLabel="Pressing this link will open a new tab where you will be able to create a ticket for the team to investigate this issue."
              >
                support ticket
              </SimLinkGenerator>{" "}
              to get help with this issue.
            </span>
          ),
          id: "message_1",
          onDismiss: () => setFlashBarItems([]),
        },
      ]);
    } else {
      setFlashBarItems([]);
    }
  }, [showUserClaimsOnly, listCargoClaimsError]);

  // Make sure user is loaded before logging metrics for regular search
  useEffect(() => {
    if (!showUserClaimsOnly && user.siteCode) {
      metricsPublisher.publishSearchResultsVisited();
    }
  }, [showUserClaimsOnly, user]);

  // Generate header content with Cargo-specific messaging
  const getHeaderContent = () => {
    if (showUserClaimsOnly) {
      const baseText = "My Submitted Cargo Claims";
      const limitText = displayData?.length >= CLAIMS_LIMIT ? ` (Most Recent ${CLAIMS_LIMIT})` : "";
      return `${baseText}${limitText}`;
    }

    return "Cargo Claims Search Results";
  };

  // Generate header actions with Cargo-specific export functionality
  const getHeaderActions = () => {
    const filePrefix = showUserClaimsOnly
      ? "my-submitted-cargo-claims"
      : "cargo_claims_search_results";
    return (
      <ExportToCSVButton
        data={displayData as Record<string, unknown>[]}
        headers={CARGO_CSV_HEADERS}
        fileNamePrefix={filePrefix}
      />
    );
  };

  // Generate search bar component (only for regular search)
  const getSearchBarComponent = () => {
    if (showUserClaimsOnly) {
      return null; // No search bar for user claims
    }
    return (
      <ClaimsSearchBar
        fields={CARGO_SEARCH_CONFIG.searchableFields}
        selectedFieldId={searchFieldId}
        term={searchInput}
        dateRange={null}
        onFieldChange={(fieldId: string) => setSearchFieldId(fieldId as SEARCH_PARAMETERS)}
        onTermChange={setSearchInput}
        onDateRangeChange={() => undefined}
        onSubmit={handleSubmit}
        onClearError={() => undefined}
      />
    );
  };

  // Generate flashbar for user claims errors
  const getFlashbarComponent = () => {
    if (showUserClaimsOnly && listCargoClaimsError && flashbarItems.length > 0) {
      return <Flashbar data-testid="error-flashbar" items={flashbarItems} />;
    }
    return null;
  };

  return (
    <>
      {getFlashbarComponent()}
      <SearchResults<CargoClaimSummary>
        items={displayData}
        columnDefinitions={CARGO_CLAIM_COLUMN_DEFINITIONS}
        loading={fetchingClaimsList}
        loadingText="Loading Cargo Claims"
        resourceName="Cargo Claim"
        headerContent={getHeaderContent()}
        headerActions={getHeaderActions()}
        searchBarComponent={getSearchBarComponent()}
        filterPlaceholder="Filter cargo claims"
        filterAriaLabel="Filter cargo claims"
        defaultSorting={{ sortingField: "createdAt", isDescending: true }}
        pageSize={10}
        minItemsForControls={5}
        testId={testId}
      />
    </>
  );
}

export default CargoClaimsHistory;
